import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import React, { useEffect, useRef, useState } from "react";
import { AssetIcon } from "../../components/AssetIcon";
import { BaseWalletIcon } from "../../base-app/components/BaseWalletIcon";
import {
  formatSolanaTokenAmount,
  parseSolanaTokenAmount,
  type SolanaTokenInfo,
} from "../../config/solana";
import {
  SOLANA_DEVNET_SWAP_USDC,
  type SolanaSwapApproval,
} from "../../joinsplit-v2/solana/swapQuote";
import type { useSolanaActions } from "../hooks/useSolanaActions";
import type { SolanaBalanceRow } from "../hooks/useSolanaNotes";
import "./solana-swap.css";

type SwapActions = Pick<
  ReturnType<typeof useSolanaActions>,
  "busy" | "getSwapQuote" | "handleSwap"
>;
const tokenKey = (token: SolanaTokenInfo) => token.mint ?? "sol";

function TokenPicker({
  id,
  label,
  token,
  options,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  token?: SolanaTokenInfo;
  options: SolanaTokenInfo[];
  disabled: boolean;
  onChange: (key: string) => void;
}) {
  return (
    <div className="solana-swap-token">
      {token && (
        <AssetIcon
          symbol={token.symbol}
          marketId={
            token.isNative
              ? "solana"
              : token.mint === SOLANA_DEVNET_SWAP_USDC
                ? "usd-coin"
                : undefined
          }
          size={32}
        />
      )}
      <select
        id={id}
        aria-label={label}
        value={token ? tokenKey(token) : ""}
        disabled={disabled || options.length === 0}
        onChange={(e) => onChange(e.target.value)}
      >
        {!token && <option value="">Select asset</option>}
        {options.map((option) => (
          <option key={tokenKey(option)} value={tokenKey(option)}>
            {option.symbol}
          </option>
        ))}
      </select>
      <BaseWalletIcon name="chevron" />
    </div>
  );
}

export function SolanaSwapTab({
  tokens,
  balances,
  balancesLoading = false,
  connected,
  actions,
  onAddFunds,
}: {
  tokens: SolanaTokenInfo[];
  balances: Pick<SolanaBalanceRow, "mint" | "spendable">[];
  balancesLoading?: boolean;
  connected: boolean;
  actions: SwapActions;
  onAddFunds?: () => void;
}) {
  const [selectedInput, setSelectedInput] = useState("");
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState(50);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [approved, setApproved] = useState<SolanaSwapApproval | null>(null);
  const [submitted, setSubmitted] = useState<{
    approval: SolanaSwapApproval;
    input: SolanaTokenInfo;
    output: SolanaTokenInfo;
    balance: bigint;
  } | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const pending = useRef<AbortController | null>(null);
  const quoteAction = useRef(actions.getSwapQuote);
  quoteAction.current = actions.getSwapQuote;
  const supported = tokens.filter(
    (token) => token.mint === null || token.mint === SOLANA_DEVNET_SWAP_USDC,
  );
  const available = supported.filter((token) =>
    balances.some((row) => row.mint === token.mint && row.spendable > 0n),
  );
  const input =
    submitted?.input ??
    available.find((token) => tokenKey(token) === selectedInput) ??
    available[0];
  const inputKey = input ? tokenKey(input) : "";
  const output =
    submitted?.output ??
    supported.find(
      (token) =>
        token.mint ===
        (input?.mint === SOLANA_DEVNET_SWAP_USDC
          ? null
          : SOLANA_DEVNET_SWAP_USDC),
    );
  const balance =
    submitted?.balance ??
    balances.find((row) => input && row.mint === input.mint)?.spendable ??
    0n;
  const outputAvailable = available.some(
    (token) => token.mint === output?.mint,
  );
  const invalidate = () => {
    pending.current?.abort();
    setLoading(false);
    setApproved(null);
    setReviewed(false);
    setError("");
  };
  useEffect(() => {
    setAmount("");
    setError("");
  }, [inputKey]);
  useEffect(() => {
    if (!approved) return;
    const timer = setTimeout(
      () => {
        setApproved(null);
        setReviewed(false);
        setRefresh((value) => value + 1);
      },
      Math.max(0, approved.quote.expiresAt - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [approved]);

  let parsedAmount = 0n;
  let amountError = "";
  if (input && amount.trim()) {
    try {
      parsedAmount = parseSolanaTokenAmount(amount, input.decimals);
      if (parsedAmount > balance)
        amountError = `Not enough private ${input.symbol}.`;
    } catch {
      amountError = `Enter an amount with up to ${input.decimals} decimal places.`;
    }
  }
  const disabled = !connected || actions.busy || !!submitted || balancesLoading;
  const validAmount = !!input && !!output && parsedAmount > 0n && !amountError;
  const inputMint = input?.mint;
  const outputMint = output?.mint;
  useEffect(() => {
    const stop = new AbortController();
    pending.current = stop;
    setApproved(null);
    setReviewed(false);
    setError("");
    setLoading(!disabled && validAmount);
    if (
      disabled ||
      !validAmount ||
      inputMint === undefined ||
      outputMint === undefined
    )
      return;
    const timer = setTimeout(async () => {
      try {
        const result = await quoteAction.current(
          inputMint,
          outputMint,
          parsedAmount,
          slippage,
          stop.signal,
        );
        if (!stop.signal.aborted) setApproved(result);
      } catch (e) {
        if (!stop.signal.aborted)
          setError(
            e instanceof Error
              ? e.message
              : "Could not get a quote. Try again.",
          );
      } finally {
        if (!stop.signal.aborted) setLoading(false);
      }
    }, AMOUNT_QUOTE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      stop.abort();
    };
  }, [
    disabled,
    validAmount,
    inputMint,
    outputMint,
    amount,
    parsedAmount,
    slippage,
    balance,
    refresh,
  ]);
  // Never confirm an earlier approval after a pair, amount or balance change.
  const approval =
    approved &&
    validAmount &&
    approved.quote.request.mint === input?.mint &&
    approved.quote.request.outputMint === output?.mint &&
    approved.quote.request.amount === parsedAmount.toString() &&
    approved.quote.request.slippageBps === slippage
      ? approved
      : null;
  // A submitted quote is a display-only receipt. It cannot become a fresh
  // approval when balances change or the quote expires during proving.
  const q = submitted?.approval.quote ?? approval?.quote;
  const feeToken = q?.feePolicy === "private-input-fees" ? input : output;
  const changeInput = (key: string) => {
    invalidate();
    setSelectedInput(key);
    setAmount("");
  };
  const useBalance = (percent: bigint) => {
    if (!input) return;
    invalidate();
    setAmount(
      formatSolanaTokenAmount((balance * percent) / 100n, input.decimals),
    );
  };

  return (
    <div className="solana-swap">
      <div className="solana-swap-toolbar">
        <span>
          <BaseWalletIcon name="lock" /> Private wallet
        </span>
        <button
          type="button"
          className="solana-swap-settings-toggle"
          aria-expanded={settingsOpen}
          aria-controls="solana-swap-settings"
          onClick={() => setSettingsOpen(!settingsOpen)}
          disabled={disabled}
        >
          <BaseWalletIcon name="settings" /> Slippage {slippage / 100}%
        </button>
      </div>
      {settingsOpen && (
        <div id="solana-swap-settings" className="solana-swap-settings">
          <label htmlFor="solana-swap-slippage">Slippage tolerance</label>
          <select
            id="solana-swap-slippage"
            value={slippage}
            disabled={disabled}
            onChange={(e) => {
              invalidate();
              setSlippage(Number(e.target.value));
            }}
          >
            <option value={50}>0.5%</option>
            <option value={100}>1%</option>
            <option value={300}>3%</option>
          </select>
        </div>
      )}
      <div className="solana-swap-card">
        <label
          htmlFor="solana-swap-input-token"
          className="solana-swap-card-label"
        >
          I want to swap
        </label>
        <div className="solana-swap-amount-row">
          <input
            id="solana-swap-amount"
            className="solana-swap-amount"
            aria-label={
              input ? `Amount to swap in ${input.symbol}` : "Amount to swap"
            }
            aria-describedby="solana-swap-available solana-swap-fee-note"
            aria-invalid={!!amountError}
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            placeholder="0"
            disabled={disabled || !input}
            onChange={(e) => {
              invalidate();
              setAmount(e.target.value);
            }}
          />
          <TokenPicker
            id="solana-swap-input-token"
            label="I want to swap"
            token={input}
            options={submitted ? [submitted.input] : available}
            disabled={disabled}
            onChange={changeInput}
          />
        </div>
        <div className="solana-swap-available" id="solana-swap-available">
          {balancesLoading && !submitted
            ? "Updating private balances…"
            : input
              ? `Available: ${formatSolanaTokenAmount(balance, input.decimals)} ${input.symbol}`
              : "No available private balance"}
        </div>
        <div
          className="solana-swap-portions"
          aria-label="Use a portion of your private balance"
        >
          {[25, 50, 100].map((percent) => (
            <button
              key={percent}
              type="button"
              disabled={disabled || !input}
              onClick={() => useBalance(BigInt(percent))}
            >
              {percent === 100 ? "Max" : `${percent}%`}
            </button>
          ))}
        </div>
      </div>
      <div className="solana-swap-direction">
        <button
          type="button"
          aria-label="Reverse swap direction"
          title={
            outputAvailable
              ? "Reverse swap direction"
              : "You need a private balance of the receiving asset to swap it"
          }
          disabled={disabled || !outputAvailable}
          onClick={() => {
            if (output) changeInput(tokenKey(output));
          }}
        >
          <BaseWalletIcon name="swap" />
        </button>
      </div>
      <div className="solana-swap-card solana-swap-receive">
        <label
          htmlFor="solana-swap-output-token"
          className="solana-swap-card-label"
        >
          And receive
        </label>
        <div className="solana-swap-amount-row">
          <output
            className="solana-swap-amount"
            aria-label="Estimated amount received"
            aria-live="polite"
          >
            {q && output
              ? formatSolanaTokenAmount(BigInt(q.outputAmount), output.decimals)
              : "—"}
          </output>
          <TokenPicker
            id="solana-swap-output-token"
            label="And receive"
            token={output}
            options={output ? [output] : []}
            disabled={disabled || !input}
            onChange={() => invalidate()}
          />
        </div>
        <p className="solana-swap-receive-hint">
          {submitted
            ? "Confirmed estimate · swap in progress"
            : loading
              ? "Getting your estimate…"
              : q
                ? "Estimated · sent to your private wallet"
                : "Enter an amount to see your estimate"}
        </p>
      </div>
      {!input && !balancesLoading && connected && (
        <div className="solana-swap-empty" role="status">
          <p>Add SOL or USDC to your private wallet to start swapping.</p>
          {onAddFunds && (
            <button type="button" onClick={onAddFunds} disabled={actions.busy}>
              Add funds
            </button>
          )}
        </div>
      )}
      <p className="solana-swap-fee-note" id="solana-swap-fee-note">
        All fees are included. Your estimated receive amount is after fees.
      </p>
      {(amountError || error) && (
        <p role="alert" className="solana-swap-error">
          {amountError || error}
        </p>
      )}
      {q && input && output && (
        <dl aria-label="Swap fee breakdown" className="solana-swap-review">
          <div>
            <dt>Total spent</dt>
            <dd>
              {formatSolanaTokenAmount(parsedAmount, input.decimals)}{" "}
              {input.symbol}
            </dd>
          </div>
          <div>
            <dt>Gas fee</dt>
            <dd>
              {formatSolanaTokenAmount(BigInt(q.fee), input.decimals)}{" "}
              {input.symbol}
            </dd>
          </div>
          <div>
            <dt>Amount sent to Orca</dt>
            <dd>
              {formatSolanaTokenAmount(
                BigInt(q.swapInputAmount),
                input.decimals,
              )}{" "}
              {input.symbol}
            </dd>
          </div>
          <div>
            <dt>Orca trading fee (included)</dt>
            <dd>
              {formatSolanaTokenAmount(
                BigInt(q.orcaTradingFee),
                input.decimals,
              )}{" "}
              {input.symbol}
            </dd>
          </div>
          <div>
            <dt>Protocol fee ({q.protocolFeeBps / 100}%)</dt>
            <dd>
              {formatSolanaTokenAmount(
                BigInt(q.protocolFee),
                feeToken!.decimals,
              )}{" "}
              {feeToken!.symbol}
            </dd>
          </div>
          {q.partnerFeeBps > 0 && (
            <div>
              <dt>Partner fee ({q.partnerFeeBps / 100}%)</dt>
              <dd>
                {formatSolanaTokenAmount(
                  BigInt(q.partnerFee),
                  feeToken!.decimals,
                )}{" "}
                {feeToken!.symbol}
              </dd>
            </div>
          )}
          <div className="solana-swap-total">
            <dt>Total received (estimated)</dt>
            <dd>
              {formatSolanaTokenAmount(BigInt(q.outputAmount), output.decimals)}{" "}
              {output.symbol}
            </dd>
          </div>
          <div>
            <dt>Minimum received</dt>
            <dd>
              {formatSolanaTokenAmount(
                BigInt(q.minOutputAmount),
                output.decimals,
              )}{" "}
              {output.symbol}
            </dd>
          </div>
        </dl>
      )}
      <p className="solana-swap-fee-note">
        Devnet test-token prices can differ from live market prices.
      </p>
      <button
        type="button"
        className="solana-swap-submit"
        disabled={
          disabled ||
          loading ||
          !validAmount ||
          (!approval && !error) ||
          (!!approval && approval.quote.expiresAt <= Date.now())
        }
        onClick={async () => {
          if (disabled) return;
          if (approval && reviewed && input && output) {
            pending.current?.abort();
            setSubmitted({ approval, input, output, balance });
            setApproved(null);
            setReviewed(false);
            try {
              await actions.handleSwap(approval);
            } finally {
              setSubmitted(null);
              setRefresh((value) => value + 1);
            }
          } else if (approval) setReviewed(true);
          else setRefresh((value) => value + 1);
        }}
      >
        {submitted || actions.busy
          ? "Swapping…"
          : loading
            ? "Getting quote…"
            : approval && reviewed
              ? "Confirm swap"
              : error
                ? "Retry quote"
                : "Review swap"}
      </button>
      <p className="solana-swap-route">
        <BaseWalletIcon name="lock" /> Tokens stay private{" "}
        <span>· Orca on Devnet</span>
      </p>
    </div>
  );
}
